/** Light post-submit Context Composer from the accepted v1.2.2 Part B board. */

import { useEffect, useState } from 'react';

import { useLanguage } from '../i18n/language.js';
import { useReducedMotion } from '../hooks/useReducedMotion.js';
import type { ContextSource } from '../lib/classifyContextUrl.js';
import type { MerchantProfileView } from '../merchant/api.js';
import { missingProfileConfirmationFields } from '../merchant/profileCompleteness.js';
import type { ComposerFlowPhase } from './ContextComposer.js';

const FLOW_COPY = {
  en: {
    handed: 'From your brief',
    brief: 'Brief',
    processing: 'Building your Business Profile',
    stages: [
      'Reading your product source',
      'Connecting your business identity',
      'Preparing your Business Profile',
    ],
    keepOpen: 'Keep this open — Mouja finishes on its own.',
    found: 'Business profile found',
    profileLabel: 'Your Business Profile for review',
    offers: 'Offers found',
    builtFrom: 'Built from your sources.',
    question: 'Does this look like your business?',
    confirm: 'Confirm business profile',
    confirming: 'Confirming…',
    notRight: 'Something’s not right',
    missing: 'What’s missing',
    insufficient: 'Not enough public evidence yet',
    insufficientBody:
      'Mouja read your sources but could not confirm the business identity. Nothing is running in the background.',
    reviewTitle: 'Mouja needs to verify this profile',
    reviewBody:
      'The sources contain uncertain or conflicting evidence. No action is required while Mouja checks it.',
    add: 'Add another source',
    replace: 'Try different sources',
    unknownNiche: 'What this business does is not established yet.',
    unknownGeo: 'Location not established yet',
    unknownOffer: 'Offer not established yet',
  },
  ar: {
    handed: 'من موجزك',
    brief: 'الموجز',
    processing: 'جاري إنشاء ملف أعمالك',
    stages: ['نقرأ مصدر منتجك', 'نربط هوية نشاطك', 'نُعِدّ ملف أعمالك'],
    keepOpen: 'أبقِ هذه الصفحة مفتوحة — موجة تكمل بنفسها.',
    found: 'تم العثور على ملف الأعمال',
    profileLabel: 'ملف أعمالك للمراجعة',
    offers: 'العروض المكتشفة',
    builtFrom: 'تم إنشاؤه من مصادرك.',
    question: 'هل هذا يشبه نشاطك التجاري؟',
    confirm: 'أكّد ملف الأعمال',
    confirming: 'جاري التأكيد…',
    notRight: 'هناك شيء غير صحيح',
    missing: 'الناقص',
    insufficient: 'لا توجد أدلة عامة كافية بعد',
    insufficientBody:
      'قرأت موجة مصادرك لكنها لم تتمكن من تأكيد هوية النشاط. لا شيء يعمل في الخلفية.',
    reviewTitle: 'تحتاج موجة إلى التحقق من هذا الملف',
    reviewBody:
      'تتضمن المصادر أدلة غير مؤكدة أو متعارضة. لا يلزمك فعل شيء بينما تتحقق موجة منها.',
    add: 'أضف مصدرًا آخر',
    replace: 'جرّب مصادر مختلفة',
    unknownNiche: 'لم يتحدد مجال هذا النشاط بعد.',
    unknownGeo: 'لم يتحدد الموقع بعد',
    unknownOffer: 'لم يتحدد العرض بعد',
  },
} as const;

function splitIdentity(identity: string): {
  readonly primary: string;
  readonly arabic: string | null;
} {
  const arabicRuns = identity.match(/[\u0600-\u06ff][\u0600-\u06ff\sـ،؛؟]+/gu);
  if (arabicRuns === null || !/[A-Za-z]/u.test(identity)) {
    return { primary: identity, arabic: null };
  }
  const arabic = arabicRuns.join(' ').trim();
  const primary = identity
    .replace(/[\u0600-\u06ff][\u0600-\u06ff\sـ،؛؟]+/gu, ' ')
    .replace(/[|/·—–-]+/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim();
  return { primary: primary || identity, arabic: arabic || null };
}

export function OnboardingComposer({
  contextNote,
  sources,
  phase,
  profileView,
  busy,
  externalError,
  onConfirmProfile,
  onProfileIncorrect,
  onRetrySources,
}: {
  readonly contextNote: string;
  readonly sources: readonly ContextSource[];
  readonly phase: Exclude<ComposerFlowPhase, 'input'>;
  readonly profileView: MerchantProfileView | null;
  readonly busy: boolean;
  readonly externalError: string | null;
  readonly onConfirmProfile: () => void;
  readonly onProfileIncorrect: () => void;
  readonly onRetrySources: () => void;
}): React.ReactElement {
  const { language } = useLanguage();
  const c = FLOW_COPY[language];
  const reducedMotion = useReducedMotion();
  const [stage, setStage] = useState(0);
  const processing = phase === 'processing';

  useEffect(() => {
    setStage(0);
    if (!processing || reducedMotion) return undefined;
    const timer = window.setInterval(() => {
      setStage((current) => Math.min(current + 1, c.stages.length - 1));
    }, 2_200);
    return () => {
      window.clearInterval(timer);
    };
  }, [c.stages.length, processing, reducedMotion]);

  const profile = profileView?.profile?.payload ?? null;
  const reviewRequired = profileView?.profileIssue.state === 'mouja_checking';
  const identity = splitIdentity(
    profile?.identity.value ?? profileView?.business.displayName ?? c.profileLabel,
  );

  return (
    <section
      className="mj-onboarding-composer"
      data-surface="landing-light"
      data-testid="context-composer-surface"
      data-state={phase}
      aria-busy={processing || busy}
      aria-label={
        phase === 'profile'
          ? c.profileLabel
          : phase === 'insufficient'
            ? c.insufficient
            : c.processing
      }
    >
      <div className="mj-onboarding-handed">
        <p className="mj-onboarding-eyebrow">{c.handed}</p>
        <ul aria-label={c.handed}>
          {sources.map((source) => (
            <li key={source.id}>
              <span aria-hidden="true" />
              <b>{source.platform}</b>
              <span dir="ltr">{source.label}</span>
            </li>
          ))}
        </ul>
        {contextNote.trim() ? (
          <p className="mj-onboarding-brief">
            <b>{c.brief}</b>
            <span>{contextNote}</span>
          </p>
        ) : null}
      </div>

      {processing ? (
        <div className="mj-onboarding-processing">
          <p className="mj-onboarding-eyebrow">{c.processing}</p>
          <div className="mj-onboarding-stage" role="status" aria-live="polite">
            <span className="mj-onboarding-pixels" aria-hidden="true" />
            <strong>{c.stages[stage]}</strong>
          </div>
          <div className="mj-onboarding-progress" aria-hidden="true">
            {c.stages.map((_, index) => (
              <span key={String(index)} data-complete={index <= stage} />
            ))}
          </div>
          <p className="mj-onboarding-meta">{c.keepOpen}</p>
        </div>
      ) : null}

      {phase === 'profile' && profile !== null ? (
        <>
          <div className="mj-onboarding-profile">
            <p className="mj-onboarding-eyebrow">{c.found}</p>
            <h2>{identity.primary}</h2>
            {identity.arabic ? (
              <p className="mj-onboarding-alt" lang="ar" dir="rtl">
                {identity.arabic}
              </p>
            ) : null}
            <p className="mj-onboarding-niche">
              {profile.niche.value ?? c.unknownNiche}
            </p>
            <div className="mj-onboarding-meta-row">
              <span className="mj-onboarding-geo">
                <i aria-hidden="true" />
                {profile.geography.value ?? c.unknownGeo}
              </span>
              <span>{c.builtFrom}</span>
            </div>
            <div className="mj-onboarding-offers">
              <p className="mj-onboarding-eyebrow">{c.offers}</p>
              <ul>
                {profile.offers.slice(0, 3).map((offer) => (
                  <li key={offer.localId}>{offer.title.value ?? c.unknownOffer}</li>
                ))}
              </ul>
            </div>
            {externalError ? (
              <p className="mj-onboarding-error" role="alert">
                {externalError}
              </p>
            ) : null}
          </div>
          <div className="mj-onboarding-actions">
            <strong>{c.question}</strong>
            <button
              type="button"
              disabled={busy || missingProfileConfirmationFields(profile).length > 0}
              onClick={onConfirmProfile}
            >
              {busy ? c.confirming : c.confirm}
            </button>
            <button
              type="button"
              className="mj-onboarding-quiet"
              disabled={busy}
              onClick={onProfileIncorrect}
            >
              Edit details
            </button>
          </div>
        </>
      ) : null}

      {phase === 'insufficient' ? (
        <>
          <div className="mj-onboarding-notice">
            <span className="mj-onboarding-pixels" aria-hidden="true" />
            <div>
              <p className="mj-onboarding-eyebrow">{c.missing}</p>
              <h2>{reviewRequired ? c.reviewTitle : c.insufficient}</h2>
              <p>
                {externalError ?? (reviewRequired ? c.reviewBody : c.insufficientBody)}
              </p>
            </div>
          </div>
          {!reviewRequired ? (
            <div className="mj-onboarding-actions">
              <button type="button" onClick={onRetrySources}>
                {c.add}
              </button>
              <button
                type="button"
                className="mj-onboarding-quiet"
                onClick={onRetrySources}
              >
                {c.replace}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
